import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getApplications, getLastAppliedJobs } from '../services/apiService';
import Footer from './Footer';
import EnhancedJobDetailsModal from './EnhancedJobDetailsModal';
import EnhancedTimelineModal from './EnhancedTimelineModal';

// Map candidate status from API to display status - matching Flutter implementation
const mapCandidateStatus = (status) => {
    const statusMap = {
        'Submitted': 'applied',
        'Under Review': 'under-review', 
        'Shortlisted': 'shortlisted',
        'Interview Scheduled': 'interview',
        'Interview': 'interview',
        'Selected': 'shortlisted',
        'Rejected': 'rejected',
        'On Hold': 'under-review',
        'Withdrawn': 'rejected',
        'Assigned': 'assigned',
        'Approved': 'approved',
        'Scheduled': 'scheduled',
        'Offered': 'offered',
        'In Process': 'in-process',
        'Removed': 'rejected',
        'No Show': 'no-show',
        'Not Joined': 'not-joined',
        'Candidate Withdrew': 'withdrawn',
        'Completed 1 Month': 'completed',
        'Hired': 'hired',
        'Position Closed': 'position-closed'
    };
    return statusMap[status] || 'applied';
};

// Centralized grouping: which internal statuses should match for a given UI filter value
const getFilterMatchStatuses = (selected) => {
    const s = String(selected || '').toLowerCase();
    const groups = {
        // UI label: Applied => include both 'applied' and 'assigned'
        'applied': ['applied', 'assigned'],
        // UI label: Shortlisted => include 'shortlisted' and 'approved'
        'shortlisted': ['shortlisted', 'approved'],
    };
    return groups[s] || (s ? [s] : []);
};

// Normalize a single status into its representative filter key
const normalizeStatusForFilter = (status) => {
    const st = String(status || '').toLowerCase();
    if (st === 'assigned') return 'applied';
    if (st === 'approved') return 'shortlisted';
    return st;
};

// Friendly labels for filter dropdown based on our mapped statuses
const getFilterLabelForStatus = (mappedStatus) => {
    const labelMap = {
        // Active
        'applied': 'Applied',
        'under-review': 'Under Review',
        'shortlisted': 'Shortlisted',
        'interview': 'Interview',
        'assigned': 'Applied',
        'approved': 'Approved',
        'selected': 'Selected',
        'scheduled': 'Interview Scheduled',
        'offered': 'Offered',
        'in-process': 'In Process',
        // Closed
        'rejected': 'Rejected',
        'no-show': 'No Show',
        'not-joined': 'Not Joined',
        'withdrawn': 'Withdrawn',
        'completed': 'Completed',
        'hired': 'Hired',
        'position-closed': 'Position Closed',
    };
    return labelMap[mappedStatus] || (mappedStatus ? mappedStatus.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) : '');
};

// Map API status to user-friendly display text - matching Flutter implementation
const getStatusDisplayText = (status) => {
    const statusLower = status.toLowerCase();
    
    switch (statusLower) {
        case 'assigned':
            return 'Applied';
        case 'approved':
            return 'Shortlisted';
        case 'selected':
            return 'Selected';
        case 'rejected':
            return 'Rejected';
        case 'scheduled':
        case 'interview scheduled':
            return 'Interview Scheduled';
        case 'offered':
            return 'Offered';
        case 'no show':
            return 'No Show';
        case 'completed 1 month':
            return 'Completed';
        case 'hired':
            return 'Hired';
        case 'candidate withdrew':
            return 'Withdrawn';
        case 'not joined':
            return 'Not Joined';
        case 'sign in pending':
            return 'Sign In Pending';
        case 'not applied yet':
            return 'Not Applied';
        case 'in process':
            return 'In Process';
        case 'position closed':
            return 'Position Closed';
        default:
            // Handle dynamic statuses like "Selected - Interview X"
            if (statusLower.startsWith('selected - interview')) {
                return status.replace('Interview', 'Round');
            }
            // Default: capitalize first letter
            return status.charAt(0).toUpperCase() + status.slice(1);
    }
};

// Determine if application is active based on status - matching Flutter logic
const isActiveApplication = (status) => {
    const activeStatuses = [
        'assigned', 'approved', 'selected', 'scheduled', 'offered', 
        'in-process', 'interview', 'applied', 'under-review', 'shortlisted'
    ];
    return activeStatuses.includes(status);
};

// Determine if application is closed based on status - matching Flutter logic
const isClosedApplication = (status) => {
    const closedStatuses = [
        'rejected', 'no-show', 'not-joined', 'withdrawn', 
        'completed', 'hired', 'position-closed'
    ];
    return closedStatuses.includes(status);
};

const statusStyles = {
    // Active statuses
    applied: { borderColor: '#2563eb', bgColor: '#dbeafe', textColor: '#1d4ed8', icon: 'fa-paper-plane' },
    'under-review': { borderColor: '#f59e0b', bgColor: '#fef3c7', textColor: '#d97706', icon: 'fa-search' },
    shortlisted: { borderColor: '#10b981', bgColor: '#dcfce7', textColor: '#16a34a', icon: 'fa-check-circle' },
    interview: { borderColor: '#8b5cf6', bgColor: '#ede9fe', textColor: '#7c3aed', icon: 'fa-user-tie' },
    assigned: { borderColor: '#06b6d4', bgColor: '#cffafe', textColor: '#0891b2', icon: 'fa-tasks' },
    approved: { borderColor: '#10b981', bgColor: '#dcfce7', textColor: '#16a34a', icon: 'fa-thumbs-up' },
    selected: { borderColor: '#10b981', bgColor: '#dcfce7', textColor: '#16a34a', icon: 'fa-star' },
    scheduled: { borderColor: '#f59e0b', bgColor: '#fef3c7', textColor: '#d97706', icon: 'fa-calendar-check' },
    offered: { borderColor: '#8b5cf6', bgColor: '#ede9fe', textColor: '#7c3aed', icon: 'fa-gift' },
    'in-process': { borderColor: '#64748b', bgColor: '#f1f5f9', textColor: '#475569', icon: 'fa-hourglass-half' },
    
    // Closed statuses
    rejected: { borderColor: '#ef4444', bgColor: '#fee2e2', textColor: '#dc2626', icon: 'fa-times-circle' },
    'no-show': { borderColor: '#f97316', bgColor: '#fed7aa', textColor: '#ea580c', icon: 'fa-user-times' },
    'not-joined': { borderColor: '#ef4444', bgColor: '#fee2e2', textColor: '#dc2626', icon: 'fa-user-slash' },
    withdrawn: { borderColor: '#f97316', bgColor: '#fed7aa', textColor: '#ea580c', icon: 'fa-undo' },
    completed: { borderColor: '#10b981', bgColor: '#dcfce7', textColor: '#16a34a', icon: 'fa-check-double' },
    hired: { borderColor: '#10b981', bgColor: '#dcfce7', textColor: '#16a34a', icon: 'fa-user-check' },
    'position-closed': { borderColor: '#6b7280', bgColor: '#f3f4f6', textColor: '#4b5563', icon: 'fa-lock' },
};

const StatCard = ({ value, label, windowWidth }) => (
    <div style={{ 
        background: 'white', 
        padding: windowWidth <= 480 ? '1rem' : '1.5rem', 
        borderRadius: windowWidth <= 480 ? '8px' : '12px', 
        boxShadow: '0 2px 8px rgba(37, 99, 235, 0.1)', 
        border: '1px solid #e0e7ff' 
    }}>
        <div style={{ 
            fontSize: windowWidth <= 480 ? '1.5rem' : '2rem', 
            fontWeight: '700', 
            color: '#2563eb', 
            marginBottom: '0.5rem' 
        }}>{value}</div>
        <div style={{ 
            fontSize: windowWidth <= 480 ? '0.75rem' : '0.875rem', 
            color: '#64748b', 
            fontWeight: '500' 
        }}>{label}</div>
    </div>
);

const ApplicationCard = ({ app, onViewDetails, onViewTimeline }) => {
    const style = statusStyles[app.mappedStatus] || statusStyles.applied;
    const displayText = getStatusDisplayText(app.originalStatus || app.candidateStatus || app.status || 'Applied');
    return (
        <div 
            className="job-card enhanced-application-card"
            data-application-id={app.id}
            data-budget-id={app.budgetId}
        >
            <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <h3 className="job-title" style={{ margin: 0 }}>{app.jobName}</h3>
                    <div style={{ background: style.bgColor, color: style.textColor, padding: '0.5rem 1rem', borderRadius: '12px', fontSize: '0.875rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className={`fas ${style.icon}`}></i>
                        {displayText}
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><i className="fas fa-map-marker-alt" style={{ color: '#ef4444', width: '14px', fontSize: '14px' }}></i> {app.location}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><i className="fas fa-calendar" style={{ color: '#2563eb', width: '14px', fontSize: '14px' }}></i> Applied: {app.appliedDate}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><i className="fas fa-clock" style={{ color: '#f59e0b', width: '14px', fontSize: '14px' }}></i> Last Updated: {(() => {
                        if (app.lastUpdatedOn) {
                            try {
                                const date = new Date(app.lastUpdatedOn);
                                return date.toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'short', 
                                    day: 'numeric' 
                                });
                            } catch (e) {
                                return app.lastUpdatedOn;
                            }
                        }
                        return 'Not available';
                    })()}</span>
                </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '0.75rem' }}>
                <button 
                    className="btn btn-outline btn-sm btn-view-details" 
                    style={{ flex: 1 }}
                    onClick={() => onViewDetails && onViewDetails(app)}
                >
                    <i className="fas fa-eye"></i> View Details
                </button>
                <button 
                    className="btn btn-primary btn-sm" 
                    style={{ flex: 1 }}
                    onClick={() => onViewTimeline && onViewTimeline(app)}
                >
                    <i className="fas fa-history"></i> View Timeline
                </button>
            </div>
        </div>
    );
};

const ApplicationSkeleton = () => (
    <div className="job-card enhanced-application-card">
        <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div style={{ background: '#f3f4f6', height: '1.5rem', width: '60%', borderRadius: '4px', animation: 'shimmer 1.5s ease-in-out infinite alternate' }}></div>
                <div style={{ background: '#f3f4f6', height: '2rem', width: '100px', borderRadius: '12px', animation: 'shimmer 1.5s ease-in-out infinite alternate' }}></div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ background: '#f3f4f6', height: '1rem', width: '40%', borderRadius: '4px', animation: 'shimmer 1.5s ease-in-out infinite alternate' }}></div>
                <div style={{ background: '#f3f4f6', height: '1rem', width: '35%', borderRadius: '4px', animation: 'shimmer 1.5s ease-in-out infinite alternate' }}></div>
                <div style={{ background: '#f3f4f6', height: '1rem', width: '30%', borderRadius: '4px', animation: 'shimmer 1.5s ease-in-out infinite alternate' }}></div>
            </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ background: '#f3f4f6', height: '2.5rem', width: '120px', borderRadius: '8px', animation: 'shimmer 1.5s ease-in-out infinite alternate' }}></div>
            <div style={{ background: '#f3f4f6', height: '2.5rem', width: '100px', borderRadius: '8px', animation: 'shimmer 1.5s ease-in-out infinite alternate' }}></div>
        </div>
    </div>
);

const Applications = () => {
    const { isAuthenticated, showSignIn, loading: authLoading } = useAuth();
    const { selectedLanguage } = useLanguage();
    
    // Redirect to sign in if not authenticated (after auth initialization completes)
    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            showSignIn();
        }
    }, [authLoading, isAuthenticated, showSignIn]);
    const [activeTab, setActiveTab] = useState('active');
    const [applications, setApplications] = useState([]);
    const [overallStatus, setOverallStatus] = useState({
        applications: 0,
        shortlists: 0,
        rejections: 0,
        interviewScheduled: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showTimelineModal, setShowTimelineModal] = useState(false);
    // Filters state
    const [availableFilters, setAvailableFilters] = useState({
        statusOptions: [], // will be overridden by tab-specific options below
        dateOptions: [],
        sortOptions: []
    });
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedDateRange, setSelectedDateRange] = useState('');
    const [selectedSortBy] = useState('');
    
    // Recent Activity API state
    const [recentActivity, setRecentActivity] = useState([]);
    const [recentLoading, setRecentLoading] = useState(false);
    const [recentError, setRecentError] = useState(null);
    
    // Handle window resize for responsive design
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    useEffect(() => {
        const fetchApplications = async () => {
            try {
                setLoading(true);
                setError(null);
                
                console.log('[Applications] Fetching applications...');
                const languageCode = selectedLanguage?.code || 'en';
                // Single fetch, no filter params; filtering will be done locally
                const response = await getApplications(1, 50, languageCode);
                
                if (response.success && response.data) {
                    console.log('[Applications] API Response:', response.data);
                    
                    // Extract overallStatus from API response
                    if (response.data.overallStatus) {
                        setOverallStatus({
                            applications: response.data.overallStatus.applications || 0,
                            shortlists: response.data.overallStatus.shortlists || 0,
                            rejections: response.data.overallStatus.rejections || 0,
                            interviewScheduled: response.data.overallStatus.interviewScheduled || 0
                        });
                    }
                    
                    // Handle the response structure from fetchCandidateApplications
                    let applicationsData = [];
                    if (response.data.applicationDetails && Array.isArray(response.data.applicationDetails)) {
                        applicationsData = response.data.applicationDetails;
                    } else if (Array.isArray(response.data)) {
                        applicationsData = response.data;
                    }
                    // Try to extract backend-provided filters (support multiple shapes)
                    const nextAvailableFilters = { statusOptions: [], dateOptions: [], sortOptions: [] };
                    const root = response.data || {};
                    const filters = root.filters || root.availableFilters || {};
                    // Status options
                    const statusFromApi = filters.statusOptions || root.statusFilters || root.statuses || [];
                    if (Array.isArray(statusFromApi) && statusFromApi.length) {
                        nextAvailableFilters.statusOptions = statusFromApi.map((opt) => {
                            if (typeof opt === 'string') return { value: opt, label: opt };
                            if (opt && typeof opt === 'object') return { value: opt.value || opt.code || opt.id || opt.name, label: opt.label || opt.name || opt.value };
                            return null;
                        }).filter(Boolean);
                    }
                    // Date options
                    const dateFromApi = filters.dateOptions || root.dateFilters || [];
                    if (Array.isArray(dateFromApi) && dateFromApi.length) {
                        nextAvailableFilters.dateOptions = dateFromApi.map((opt) => {
                            if (typeof opt === 'string') return { value: opt, label: opt };
                            if (opt && typeof opt === 'object') return { value: opt.value || opt.code || opt.id || opt.name, label: opt.label || opt.name || opt.value };
                            return null;
                        }).filter(Boolean);
                    }
                    // Sort options
                    const sortFromApi = filters.sortOptions || root.sortFilters || [];
                    if (Array.isArray(sortFromApi) && sortFromApi.length) {
                        nextAvailableFilters.sortOptions = sortFromApi.map((opt) => {
                            if (typeof opt === 'string') return { value: opt, label: opt };
                            if (opt && typeof opt === 'object') return { value: opt.value || opt.code || opt.id || opt.name, label: opt.label || opt.name || opt.value };
                            return null;
                        }).filter(Boolean);
                    }
                    
                    console.log('[Applications] Processing applications:', applicationsData.length);
                    
                    // Map API data to component format
                    const mappedApplications = applicationsData.map((app, index) => {
                        // Parse salary
                        let salaryText = 'Salary not specified';
                        if (app.maxSalaryLimit && typeof app.maxSalaryLimit === 'number') {
                            salaryText = `₹${app.maxSalaryLimit.toLocaleString()}/month`;
                        } else if (app.salary && typeof app.salary === 'number') {
                            salaryText = `₹${app.salary.toLocaleString()}/month`;
                        }
                        
                        // Parse applied date
                        let appliedDate = 'Date not available';
                        if (app.appliedOn) {
                            try {
                                const date = new Date(app.appliedOn);
                                appliedDate = date.toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'short', 
                                    day: 'numeric' 
                                });
                            } catch (e) {
                                console.warn('[Applications] Invalid date format:', app.appliedOn);
                            }
                        } else if (app.appliedDate) {
                            try {
                                const date = new Date(app.appliedDate);
                                appliedDate = date.toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'short', 
                                    day: 'numeric' 
                                });
                            } catch (e) {
                                console.warn('[Applications] Invalid date format:', app.appliedDate);
                            }
                        }
                        
                        // Preserve original status from API
                        const originalStatus = app.candidateStatus || app.status || 'Submitted';
                        
                        // Map status
                        const mappedStatus = mapCandidateStatus(originalStatus);
                        
                        // Determine if application is active or closed - matching Flutter logic
                        const isActive = isActiveApplication(mappedStatus);
                        const isClosed = isClosedApplication(mappedStatus);
                        
                        return {
                            // Preserve all original API data
                            ...app,
                            // Add formatted fields for display
                            id: app.id || app.applicationId || `app-${index}`,
                            title: app.jobName || app.jobTitle || 'Unknown Job',
                            jobTitle: app.jobTitle || app.jobName || 'Unknown Job',
                            jobName: app.jobName || 'Job name not specified',
                            location: (() => {
                                const city = app.cityName || app.location || 'Location not specified';
                                const pinCode = app.pincode || app.pinCode || app.zipCode || app.postalCode;
                                return pinCode ? `${city} - ${pinCode}` : city;
                            })(),
                            salary: salaryText,
                            appliedDate: appliedDate,
                            status: mappedStatus,
                            originalStatus: originalStatus, // Store original API status for display
                            mappedStatus: mappedStatus, // Store mapped status for styling
                            isActive: isActive,
                            isClosed: isClosed,
                            company: app.companyName || app.company || 'Channelplay',
                            jobId: app.jobId || '',
                            applicationId: app.applicationId || app.id,
                            lastUpdatedOn: app.lastUpdatedOn || app.appliedOn || app.appliedDate
                        };
                    });
                    
                    console.log('[Applications] Mapped applications:', mappedApplications);
                    setApplications(mappedApplications);

                    // Fallback: derive status options from data if backend didn't provide
                    if (nextAvailableFilters.statusOptions.length === 0 && mappedApplications.length > 0) {
                        const uniqueStatuses = Array.from(new Set(mappedApplications.map(a => a.status)));
                        nextAvailableFilters.statusOptions = [{ value: '', label: 'All' }, ...uniqueStatuses.map(s => ({ value: s, label: s.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) }))];
                    }
                    // Provide sensible defaults if missing
                    if (nextAvailableFilters.dateOptions.length === 0) {
                        nextAvailableFilters.dateOptions = [
                            { value: '', label: 'All Time' },
                            { value: 'LAST_7_DAYS', label: 'Last 7 days' },
                            { value: 'LAST_30_DAYS', label: 'Last 30 days' },
                            { value: 'LAST_90_DAYS', label: 'Last 3 months' },
                        ];
                    }
                    if (nextAvailableFilters.sortOptions.length === 0) {
                        nextAvailableFilters.sortOptions = [
                            { value: 'RECENT', label: 'Most Recent' },
                            { value: 'OLDEST', label: 'Oldest First' },
                            { value: 'STATUS', label: 'Status' },
                        ];
                    }
                    setAvailableFilters(nextAvailableFilters);
                } else {
                    console.warn('[Applications] No applications data in response:', response);
                    setApplications([]);
                }
            } catch (err) {
                console.error('[Applications] Error fetching applications:', err);
                setError('Failed to load applications. Please try again.');
                setApplications([]);
            } finally {
                setLoading(false);
            }
        };
        
        if (isAuthenticated) {
            // Throttle to once per page load (and avoid React StrictMode double-invoke)
            const key = 'applications_fetched_at';
            const now = Date.now();
            let lastTs = 0;
            try {
                lastTs = parseInt(sessionStorage.getItem(key) || '0', 10) || 0;
            } catch (_) {}
            const TTL_MS = 10000; // 10 seconds window to coalesce duplicate calls
            if (!lastTs || (now - lastTs) > TTL_MS) {
                try { sessionStorage.setItem(key, String(now)); } catch (_) {}
                fetchApplications();
            } else {
                // Skip duplicate fetch within TTL window
            }
        }
    }, [isAuthenticated, selectedLanguage?.code]);

    // Fetch recent activity (last applied jobs)
    useEffect(() => {
        const fetchRecent = async () => {
            try {
                setRecentLoading(true);
                setRecentError(null);
                // Default size 5
                const resp = await getLastAppliedJobs(undefined, 5);
                // Normalize response data shape
                const data = (resp && typeof resp === 'object' && 'data' in resp) ? resp.data : resp;

                // Extract array payloads if present for visual list
                let items = [];
                if (Array.isArray(data)) {
                    items = data;
                } else if (data && typeof data === 'object') {
                    if (Array.isArray(data.lastAppliedJobs)) items = data.lastAppliedJobs;
                    else if (Array.isArray(data.items)) items = data.items;
                    else if (Array.isArray(data.content)) items = data.content; // common paged shape
                }

                // Normalize for UI list (best-effort)
                const normalized = items.map((it, idx) => ({
                    key: it.applicationId || it.id || `recent-${idx}`,
                    jobName: it.budgetName || it.jobName || it.jobTitle || 'Unknown Job',
                    company: it.companyName || it.company || 'Channelplay',
                    appliedOn: it.appliedOn || it.appliedDate || it.lastUpdatedOn,
                    status: mapCandidateStatus(it.candidateStatus || it.status || 'Submitted'),
                    candidateStatus: it.candidateStatus || it.status || 'Submitted'
                }));
                setRecentActivity(normalized.slice(0, 5));
            } catch (e) {
                setRecentActivity([]);
                setRecentError('Failed to load recent activity');
            } finally {
                setRecentLoading(false);
            }
        };
        // Throttle to once per page load (and avoid React StrictMode double-invoke)
        if (isAuthenticated) {
            const key = 'last_applied_jobs_fetched_at';
            const now = Date.now();
            let lastTs = 0;
            try {
                lastTs = parseInt(sessionStorage.getItem(key) || '0', 10) || 0;
            } catch (_) {}
            const TTL_MS = 10000; // 10 seconds window to coalesce duplicate calls
            if (!lastTs || (now - lastTs) > TTL_MS) {
                try { sessionStorage.setItem(key, String(now)); } catch (_) {}
                fetchRecent();
            } else {
                // Skip duplicate fetch within TTL window
                // Keep recentLoading as false and leave previous data as-is
            }
        }
    }, [isAuthenticated]);

    // Handle highlight query parameter from notification clicks
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const highlightId = urlParams.get('highlight') || sessionStorage.getItem('pendingHighlight');
        
        if (highlightId && applications.length > 0) {
            // Clear the pending highlight from sessionStorage
            sessionStorage.removeItem('pendingHighlight');
            console.log('[Applications] Looking for application to highlight with ID:', highlightId);
            
            // Find the application by position ID, budget ID, or application ID
            const targetApp = applications.find(app => 
                app.budgetId === highlightId || 
                app.jobId === highlightId || 
                app.applicationId === highlightId ||
                app.id === highlightId ||
                String(app.budgetId) === String(highlightId) ||
                String(app.jobId) === String(highlightId) ||
                String(app.applicationId) === String(highlightId) ||
                String(app.id) === String(highlightId)
            );
            
            if (targetApp) {
                console.log('[Applications] Found target application:', targetApp);
                
                // Switch to the appropriate tab based on application status
                if (targetApp.isActive) {
                    setActiveTab('active');
                } else if (targetApp.isClosed) {
                    setActiveTab('closed');
                }
                
                // Clear any filters to ensure the application is visible
                setSelectedStatus('');
                setSelectedDateRange('');
                
                // Scroll to the highlighted application card after a short delay to ensure tab switch completes
                setTimeout(() => {
                    // Find the application card element and scroll to it
                    const cardElement = document.querySelector(`[data-application-id="${targetApp.id}"], [data-application-id="${targetApp.applicationId}"], [data-budget-id="${targetApp.budgetId}"]`);
                    if (cardElement) {
                        cardElement.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'center' 
                        });
                        
                        // Add a highlight effect to make the card stand out
                        cardElement.style.boxShadow = '0 0 20px rgba(37, 99, 235, 0.5)';
                        cardElement.style.transform = 'scale(1.02)';
                        cardElement.style.transition = 'all 0.3s ease';
                        
                        // Remove highlight effect after 3 seconds
                        setTimeout(() => {
                            cardElement.style.boxShadow = '';
                            cardElement.style.transform = '';
                        }, 3000);
                    }
                    
                    // Clean up the URL parameter
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, '', newUrl);
                }, 100);
            } else {
                console.warn('[Applications] Could not find application with highlight ID:', highlightId);
                // Clean up the URL parameter even if not found
                const newUrl = window.location.pathname;
                window.history.replaceState({}, '', newUrl);
            }
        }
    }, [applications]);

    // Compute filtered list locally based on active tab and selected filters
    const filteredApplications = useMemo(() => {
        let list = Array.isArray(applications) ? [...applications] : [];
        // Tab filter
        if (activeTab === 'active') {
            list = list.filter(a => a.isActive);
        } else if (activeTab === 'closed') {
            list = list.filter(a => a.isClosed);
        }
        // Status filter (supports grouped UI labels)
        if (selectedStatus) {
            const matches = getFilterMatchStatuses(selectedStatus);
            list = list.filter(a => matches.includes(String(a.status || '').toLowerCase()));
        }
        // Date range filter (appliedOn/lastUpdatedOn)
        const parseDate = (d) => d ? new Date(d) : null;
        let fromDate = null, toDate = null;
        const now = new Date();
        switch (selectedDateRange) {
            case 'LAST_7_DAYS':
                fromDate = new Date(now);
                fromDate.setDate(now.getDate() - 7);
                break;
            case 'LAST_30_DAYS':
                fromDate = new Date(now);
                fromDate.setDate(now.getDate() - 30);
                break;
            case 'LAST_90_DAYS':
                fromDate = new Date(now);
                fromDate.setDate(now.getDate() - 90);
                break;
            default:
                break;
        }
        if (fromDate || toDate) {
            list = list.filter(a => {
                const d = parseDate(a.appliedOn || a.lastUpdatedOn);
                if (!d) return false;
                if (fromDate && d < fromDate) return false;
                if (toDate && d > toDate) return false;
                return true;
            });
        }
        // Sort
        if (selectedSortBy === 'OLDEST') {
            list.sort((a, b) => new Date(a.appliedOn || a.lastUpdatedOn) - new Date(b.appliedOn || b.lastUpdatedOn));
        } else if (selectedSortBy === 'STATUS') {
            list.sort((a, b) => (a.status || '').localeCompare(b.status || ''));
        } else { // RECENT or default
            list.sort((a, b) => new Date(b.appliedOn || b.lastUpdatedOn) - new Date(a.appliedOn || a.lastUpdatedOn));
        }
        return list;
    }, [applications, activeTab, selectedStatus, selectedDateRange, selectedSortBy]);

    // Counts for tab badges that also respect filters (status/date)
    const filteredActiveCount = useMemo(() => {
        let list = Array.isArray(applications) ? [...applications] : [];
        list = list.filter(a => a.isActive);
        if (selectedStatus) {
            const matches = getFilterMatchStatuses(selectedStatus);
            list = list.filter(a => matches.includes(String(a.status || '').toLowerCase()));
        }
        // Date range filter
        const parseDate = (d) => d ? new Date(d) : null;
        let fromDate = null, toDate = null;
        const now = new Date();
        switch (selectedDateRange) {
            case 'LAST_7_DAYS':
                fromDate = new Date(now);
                fromDate.setDate(now.getDate() - 7);
                break;
            case 'LAST_30_DAYS':
                fromDate = new Date(now);
                fromDate.setDate(now.getDate() - 30);
                break;
            case 'LAST_90_DAYS':
                fromDate = new Date(now);
                fromDate.setDate(now.getDate() - 90);
                break;
            default:
                break;
        }
        if (fromDate || toDate) {
            list = list.filter(a => {
                const d = parseDate(a.appliedOn || a.lastUpdatedOn);
                if (!d) return false;
                if (fromDate && d < fromDate) return false;
                if (toDate && d > toDate) return false;
                return true;
            });
        }
        return list.length;
    }, [applications, selectedStatus, selectedDateRange]);

    const filteredClosedCount = useMemo(() => {
        let list = Array.isArray(applications) ? [...applications] : [];
        list = list.filter(a => a.isClosed);
        if (selectedStatus) {
            const matches = getFilterMatchStatuses(selectedStatus);
            list = list.filter(a => matches.includes(String(a.status || '').toLowerCase()));
        }
        // Date range filter
        const parseDate = (d) => d ? new Date(d) : null;
        let fromDate = null, toDate = null;
        const now = new Date();
        switch (selectedDateRange) {
            case 'LAST_7_DAYS':
                fromDate = new Date(now);
                fromDate.setDate(now.getDate() - 7);
                break;
            case 'LAST_30_DAYS':
                fromDate = new Date(now);
                fromDate.setDate(now.getDate() - 30);
                break;
            case 'LAST_90_DAYS':
                fromDate = new Date(now);
                fromDate.setDate(now.getDate() - 90);
                break;
            default:
                break;
        }
        if (fromDate || toDate) {
            list = list.filter(a => {
                const d = parseDate(a.appliedOn || a.lastUpdatedOn);
                if (!d) return false;
                if (fromDate && d < fromDate) return false;
                if (toDate && d > toDate) return false;
                return true;
            });
        }
        return list.length;
    }, [applications, selectedStatus, selectedDateRange]);

    // Helpers for dates (none currently needed)

    // Format relative time (e.g., 2h ago)
    const timeAgo = (date) => {
        if (!date) return '';
        const now = new Date();
        const diffMs = now - new Date(date);
        const sec = Math.floor(diffMs / 1000);
        const min = Math.floor(sec / 60);
        const hr = Math.floor(min / 60);
        const day = Math.floor(hr / 24);
        if (day > 0) return `${day}d ago`;
        if (hr > 0) return `${hr}h ago`;
        if (min > 0) return `${min}m ago`;
        return 'just now';
    };

    // Recent Activity items (no unused computed memos)

    // Status options based on the active tab
    const statusOptionsForTab = useMemo(() => {
        let base = Array.isArray(applications) ? applications : [];
        base = activeTab === 'closed' ? base.filter(a => a.isClosed) : base.filter(a => a.isActive);
        // Normalize option values to their UI group representative
        const normalized = base
            .map(a => normalizeStatusForFilter(a.status))
            .filter(Boolean);
        const uniqueMappedStatuses = Array.from(new Set(normalized));
        return [
            { value: '', label: 'All' },
            ...uniqueMappedStatuses.map((s) => ({ value: s, label: getFilterLabelForStatus(s) }))
        ];
    }, [applications, activeTab]);

    // Ensure selectedStatus remains valid when tab or options change
    useEffect(() => {
        if (!statusOptionsForTab.some(o => o.value === selectedStatus)) {
            setSelectedStatus('');
        }
    }, [statusOptionsForTab, selectedStatus]);

    // Removed duplicate sign-in modal effect (now gated above with authLoading)

    // Handler for viewing application details
    const handleViewDetails = (application) => {
        setSelectedApplication(application);
        setShowDetailModal(true);
        const id = application.id || application.applicationId;
        if (id) {
            const idStr = String(id);
            const nowIso = new Date().toISOString();
            const snapshot = { id: idStr, jobName: application.jobName, company: application.company };
            try { 
                localStorage.setItem('recently_viewed_application_id', idStr);
                localStorage.setItem('recently_viewed_application_at', nowIso);
                localStorage.setItem('recently_viewed_application_snapshot', JSON.stringify(snapshot));
            } catch {}
        }
    };

    // Handler for viewing application timeline
    const handleViewTimeline = (application) => {
        setSelectedApplication(application);
        setShowTimelineModal(true);
        const id = application.id || application.applicationId;
        if (id) {
            try { 
                localStorage.setItem('recently_viewed_application_id', id);
                localStorage.setItem('recently_viewed_application_at', new Date().toISOString());
            } catch {}
        }
    };

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setSelectedApplication(null);
    };

    const handleCloseTimelineModal = () => {
        setShowTimelineModal(false);
        setSelectedApplication(null);
    };

    // Show the page content normally - the modal will appear on top when needed

    return (
        <div style={{ background: '#f8fafc' }}>
            <section style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #2563eb 100%)', color: 'white', padding: windowWidth <= 480 ? '1.5rem 0' : windowWidth <= 768 ? '2rem 16px' : '2.5rem 24px', position: 'relative', overflow: 'hidden', boxSizing: 'border-box' }}>
                <div style={{ position: 'relative', zIndex: 1, textAlign: 'left', margin: windowWidth <= 480 ? '0 16px' : '0', boxSizing: 'border-box', width: windowWidth <= 480 ? 'calc(100% - 32px)' : '100%', maxWidth: '100%' }}>
                    <h1 style={{ fontSize: windowWidth <= 480 ? '0.9rem' : windowWidth <= 768 ? '1.75rem' : '2.25rem', marginBottom: '0.5rem', fontWeight: 700, lineHeight: 1.1, padding: '0', whiteSpace: 'nowrap', width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                        <i className="fas fa-file-alt" style={{ color: 'rgba(255,255,255,0.9)', fontSize: windowWidth <= 480 ? '0.8rem' : windowWidth <= 768 ? '1.5rem' : '2.25rem', marginRight: windowWidth <= 480 ? '8px' : '0.5rem' }}></i>My Applications
                    </h1>
                    <p style={{ fontSize: windowWidth <= 480 ? '0.8rem' : windowWidth <= 768 ? '0.875rem' : '1rem', opacity: 0.9, fontWeight: 400, lineHeight: '1.4', margin: '0', padding: '0' }}>Track and manage all your job applications in one place</p>
                </div>
            </section>

            <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', padding: windowWidth <= 480 ? '1rem 20px 0 20px' : windowWidth <= 768 ? '1.5rem 16px 0 16px' : '2rem 24px 0 24px', borderBottom: '1px solid #e0e7ff', boxSizing: 'border-box' }}>
                <div style={{ display: 'grid', gridTemplateColumns: windowWidth <= 480 ? 'repeat(2, 1fr)' : windowWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: windowWidth <= 480 ? '0.75rem' : '1.5rem', textAlign: 'center', marginBottom: windowWidth <= 480 ? '1rem' : '1.5rem' }}>
                    <StatCard 
                        value={loading ? '-' : overallStatus.applications.toString()} 
                        label="Submitted" 
                        windowWidth={windowWidth}
                    />
                    <StatCard 
                        value={loading ? '-' : applications.filter(a => ['shortlisted', 'approved', 'selected'].includes(a.mappedStatus)).length.toString()} 
                        label="Shortlisted" 
                        windowWidth={windowWidth}
                    />
                    <StatCard 
                        value={loading ? '-' : applications.filter(a => ['rejected', 'withdrawn', 'no-show', 'not-joined'].includes(a.mappedStatus)).length.toString()} 
                        label="Rejected" 
                        windowWidth={windowWidth}
                    />
                    <StatCard 
                        value={loading ? '-' : applications.filter(a => ['interview', 'scheduled'].includes(a.mappedStatus)).length.toString()} 
                        label="Interviews" 
                        windowWidth={windowWidth}
                    />
                </div>
            </div>

            {/* Right Column: Recent Activity card (as in screenshot) */}

            <div className="main-content" style={{display: 'grid', gridTemplateColumns: windowWidth <= 768 ? '1fr' : '250px 1fr 300px', gap: windowWidth <= 480 ? '12px' : windowWidth <= 768 ? '16px' : '24px', padding: windowWidth <= 480 ? '0 20px 0 20px' : windowWidth <= 768 ? '0 16px 0 16px' : '0 24px 0 24px', alignItems: 'flex-start', width: '100%', maxWidth: '100%', boxSizing: 'border-box'}}>
                {windowWidth > 768 && (
                    <aside className="left-sidebar">
                        <div className="sidebar-section">
                            <h3>Filter Applications</h3>
                            
                            <div className="filter-group">
                                <label>Status</label>
                                <select 
                                    className="filter-input"
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                    {statusOptionsForTab.map((opt, idx) => (
                                        <option key={`status-${idx}-${opt.value}`} value={opt.value ?? ''}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="filter-group">
                                <label>Date Applied</label>
                                <select 
                                    className="filter-input"
                                    value={selectedDateRange}
                                    onChange={(e) => setSelectedDateRange(e.target.value)}
                                >
                                    {availableFilters.dateOptions.map((opt, idx) => (
                                        <option key={`date-${idx}-${opt.value}`} value={opt.value ?? ''}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </aside>
                )}

                <main className="job-listings" style={{
                    background: 'white',
                    padding: windowWidth <= 480 ? '16px' : '24px',
                    borderRadius: windowWidth <= 480 ? '6px' : '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ display: 'flex', gap: '0', marginBottom: windowWidth <= 480 ? '1rem' : '1.5rem', alignItems: 'center', width: '100%' }}>
                        <button className={`simple-tab-btn ${activeTab === 'active' ? 'active' : ''}`} onClick={() => setActiveTab('active')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: windowWidth <= 480 ? '0.25rem' : '0.5rem', padding: windowWidth <= 480 ? '0.5rem 0.75rem' : '0.75rem 1rem', borderRadius: windowWidth <= 480 ? '8px 0 0 8px' : '12px 0 0 12px', cursor: 'pointer', fontWeight: 600, fontSize: windowWidth <= 480 ? '0.75rem' : '0.875rem', transition: 'all 0.3s ease', flex: 1, border: 'none' }}>
                            <i className="fas fa-clock" style={{ fontSize: windowWidth <= 480 ? '0.75rem' : '1rem' }}></i>
                            <span>{windowWidth <= 480 ? 'Active' : 'Active Applications'}</span>
                            <span style={{ padding: '0.25rem 0.5rem', borderRadius: windowWidth <= 480 ? '8px' : '12px', fontSize: windowWidth <= 480 ? '0.65rem' : '0.75rem', fontWeight: 700 }}>{loading ? '-' : filteredActiveCount}</span>
                        </button>
                        <button className={`simple-tab-btn ${activeTab === 'closed' ? 'active' : ''}`} onClick={() => setActiveTab('closed')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: windowWidth <= 480 ? '0.25rem' : '0.5rem', padding: windowWidth <= 480 ? '0.5rem 0.75rem' : '0.75rem 1rem', borderRadius: windowWidth <= 480 ? '0 8px 8px 0' : '0 12px 12px 0', cursor: 'pointer', fontWeight: 600, fontSize: windowWidth <= 480 ? '0.75rem' : '0.875rem', transition: 'all 0.3s ease', flex: 1, border: 'none' }}>
                            <i className="fas fa-archive" style={{ fontSize: windowWidth <= 480 ? '0.75rem' : '1rem' }}></i>
                            <span>{windowWidth <= 480 ? 'Closed' : 'Closed Applications'}</span>
                            <span style={{ padding: '0.25rem 0.5rem', borderRadius: windowWidth <= 480 ? '8px' : '12px', fontSize: windowWidth <= 480 ? '0.65rem' : '0.75rem', fontWeight: 700 }}>{loading ? '-' : filteredClosedCount}</span>
                        </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {loading ? (
                            // Show skeleton loaders while loading
                            Array.from({ length: 6 }, (_, index) => (
                                <ApplicationSkeleton key={`skeleton-${index}`} />
                            ))
                        ) : error ? (
                            // Show error message
                            <div style={{ 
                                padding: '2rem', 
                                textAlign: 'center', 
                                background: '#fee2e2', 
                                borderRadius: '12px', 
                                border: '1px solid #fecaca' 
                            }}>
                                <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', color: '#dc2626', marginBottom: '1rem' }}></i>
                                <h3 style={{ color: '#dc2626', marginBottom: '0.5rem' }}>Error Loading Applications</h3>
                                <p style={{ color: '#991b1b', marginBottom: '1rem' }}>{error}</p>
                                <button 
                                    onClick={() => window.location.reload()} 
                                    style={{ 
                                        background: '#dc2626', 
                                        color: 'white', 
                                        padding: '0.75rem 1.5rem', 
                                        borderRadius: '8px', 
                                        border: 'none', 
                                        cursor: 'pointer' 
                                    }}
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : applications.length === 0 ? (
                            // Show empty state
                            <div style={{ 
                                padding: '3rem', 
                                textAlign: 'center', 
                                background: '#f8fafc', 
                                borderRadius: '12px', 
                                border: '1px solid #e2e8f0' 
                            }}>
                                <i className="fas fa-briefcase" style={{ fontSize: '3rem', color: '#94a3b8', marginBottom: '1rem' }}></i>
                                <h3 style={{ color: '#475569', marginBottom: '0.5rem' }}>No Applications Yet</h3>
                                <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>You haven't applied to any jobs yet. Start exploring opportunities!</p>
                                <button 
                                    onClick={() => window.location.href = '/'} 
                                    style={{ 
                                        background: '#2563eb', 
                                        color: 'white', 
                                        padding: '0.75rem 1.5rem', 
                                        borderRadius: '8px', 
                                        border: 'none', 
                                        cursor: 'pointer' 
                                    }}
                                >
                                    Browse Jobs
                                </button>
                            </div>
                        ) : (
                            // Show applications using local filters (tab/status/date/sort)
                            filteredApplications.map((app, index) => (
                                <ApplicationCard 
                                    key={app.id || app.applicationId || index} 
                                    app={app} 
                                    onViewDetails={handleViewDetails} 
                                    onViewTimeline={handleViewTimeline}
                                />
                            ))
                        )}
                    </div>
                </main>

                {windowWidth > 768 && (
                    <aside>
                        <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '16px', boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)' }}>
                            <div style={{ padding: '1rem 1rem 0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid #f1f5f9' }}>
                                <div style={{ width: 28, height: 28, borderRadius: '9999px', background: '#e0ecff', display: 'grid', placeItems: 'center', color: '#2563eb' }}>
                                    <i className="fas fa-bolt"></i>
                                </div>
                                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1rem', fontWeight: 700 }}>Recent Activity</h3>
                            </div>
                        <div style={{ padding: '0.75rem 1rem' }}>
                            {/* Recent applications from API */}
                            {recentLoading && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                    {Array.from({ length: 3 }).map((_, i) => (
                                        <div key={`recent-skel-${i}`} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                            <div style={{ width: 36, height: 36, borderRadius: '9999px', background: '#f1f5f9' }}></div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ height: 12, background: '#f1f5f9', borderRadius: 6, marginBottom: 6 }}></div>
                                                <div style={{ height: 10, width: '60%', background: '#f1f5f9', borderRadius: 6 }}></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {recentError && (
                                <div style={{ background: '#fff7ed', border: '1px solid #ffedd5', color: '#9a3412', padding: '0.5rem 0.75rem', borderRadius: 8, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                                    <i className="fas fa-triangle-exclamation" style={{ marginRight: 6 }}></i>
                                    {recentError}
                                </div>
                            )}
                            {!recentLoading && !recentError && recentActivity.length > 0 && (
                                <div style={{ marginBottom: '0.5rem' }}>
                                    {recentActivity.map((item) => (
                                        <div key={item.key} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.75rem', borderRadius: '12px', background: '#f8fafc', marginBottom: '0.5rem' }}>
                                            <div style={{ width: 36, height: 36, borderRadius: '9999px', background: '#fff7ed', display: 'grid', placeItems: 'center', color: '#d97706' }}>
                                                <i className="fas fa-file-alt"></i>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>{getStatusDisplayText(item.candidateStatus)}</div>
                                                <div style={{ color: '#475569', fontSize: '0.85rem' }}>{item.jobName} at {item.company}</div>
                                            </div>
                                            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{timeAgo(item.appliedOn)}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </aside>
                )}
            </div>
            <Footer />
            
            {/* Enhanced Job Details Modal */}
            {selectedApplication && (
                <EnhancedJobDetailsModal
                    isOpen={showDetailModal}
                    onClose={handleCloseDetailModal}
                    application={selectedApplication}
                />
            )}

            {/* Enhanced Timeline Modal */}
            {selectedApplication && (
                <EnhancedTimelineModal
                    isOpen={showTimelineModal}
                    onClose={handleCloseTimelineModal}
                    application={selectedApplication}
                />
            )}
        </div>
    );
};

export default Applications;
